import api from 'services/api';
import { array, enforce, num, nullable, object, runSchema, str, unknownRecord, withDefault } from 'validation';

/**
 * A single connection as returned by nap-backend
 * `GET /connections/:connection_id` (routes/connections.js — secrets removed,
 * `workers` + `folder_path` attached). This is the edit-relevant projection
 * re-derived from the backend source; the endpoint also returns `created_at`,
 * `updated_at` and `folder_path`, which this modal does not read, so the object
 * parser drops them. Assignable to the slice `Connection` shape the modal
 * accepts. Deeply readonly — validated wire data.
 */
export interface FetchedConnection {
  readonly connection_id: number;
  readonly connection_name: string;
  readonly connection_type: string;
  readonly connection_parameters: Record<string, unknown>;
  // Mutable-outer: the modal assigns workers straight into its form state.
  readonly workers: { readonly worker_id: number; readonly worker_name: string; readonly priority: number }[];
  readonly folder_id: number | null;
}

const connectionSchema = object<FetchedConnection>({
  connection_id: num(),
  connection_name: str(),
  connection_type: str(),
  // Per-connection-type dynamic JSON (secrets stripped server-side); the server
  // is the validation authority and the modal hands it to type-specific
  // sub-forms that dereference it. Validated only as "an object" (contents pass
  // through verbatim); a missing/null payload defaults to `{}` so the sub-forms
  // never dereference null.
  connection_parameters: withDefault(unknownRecord(), {}),
  workers: array(object<{ worker_id: number; worker_name: string; priority: number }>({ worker_id: num(), worker_name: str(), priority: num() })),
  folder_id: nullable(num()),
});

/**
 * Fetch a single connection by id for the edit form when the list slice is
 * empty (e.g. launched from the Workspace). Boundary-validated (issue #757 step
 * 3); structural failure or a drifted identity (`connection_id`) yields null so
 * the caller surfaces a "not found" instead of opening the modal on a degraded
 * record.
 */
export const getConnection = async (id: number): Promise<FetchedConnection | null> => {
  try {
    const res = await api.get(`/connections/${id}`);
    const result = runSchema(connectionSchema, res.data, 'getConnection');
    if (!result.ok) return null;
    return enforce(result.value, result.warnings, ['connection_id']);
  } catch {
    return null;
  }
};
